import {get, uniq, merge, isArray} from 'lodash';
import {default as es6compile} from 'es6-template-strings/compile';
import {default as es6resolve} from 'es6-template-strings/resolve';
import en from './en';
import hk from './hk';
import cn from './cn';

class i18n {
  constructor(options) {
    if (typeof options === 'object') {
      merge(this, options);
    }
  }

  lang = {en, hk, cn};

  local = 'en';
  fallbackLocal = 'en';

  /**
   * get translated string
   * @param key
   * @param [def] default will return the key
   * @return {*}
   */
  s(key, def) {
    if (typeof key === 'string' || isArray(key)) {
      for (let l of [this.local, this.fallbackLocal]) {
        let value = get(this.lang[l], key, undefined);
        if (value !== undefined) {
          return value;
        }
      }
      return def === undefined ? key : def; // default
    }
    return key;
  }

  /**
   * get translated string
   * @param {Object|String} dict the input dictionary, which contains values with language codes
   * as keys, e.g. <code>{ en: 'hello', hk: 'world' }</code><p>
   * if dict is a string, it will be returned as is.
   * @param {*} [def] default value if not found
   * @return {*}
   */
  S(dict, def) {
    if (typeof dict === 'string') {
      return dict;
    } else if (typeof dict === 'object' && dict) {
      for (let l of [this.local, this.fallbackLocal]) {
        let value = get(dict, l, undefined);
        if (value !== undefined) {
          return value;
        }
      }
    }
    return def; // default
  }

  /**
   * 'Not Null'
   * ES6 tagged template function
   * omit null and undefined values
   * @param {Array.<String>} literals
   * @param {...*} substitutions
   * @return {*}
   */
  nn(literals, ...substitutions) {
    let res = literals[0];
    for (let i = 1; i < literals.length; i++) {
      if (arguments[i] != null) // you might want to handle `0` different
        res += arguments[i];
      res += literals[i];
    }
    return res;
  }

  /**
   * get translated string with formatting, using ES6 template
   * @param {*} translationKey the translated string will be treated as ES6 template syntax,
   * e.g. `Hello ${something}`
   * @param {Object} [args] key-values to apply. a dynamic dictionary object {@link #dict} will be inserted as
   * 'i18n' if the key 'i18n' is not given in args
   * @param {Object} [options] template compilation options
   */
  f(translationKey, args, options) {
    translationKey = this.s(translationKey);
    if (typeof translationKey === 'string') {
      let fn = es6compile(translationKey);
      args = args || {};
      if (args['i18n'] === undefined) {
        args['i18n'] = this.dict;
      }
      return this.nn.apply(this, es6resolve(fn, args, options));
    } else {
      return translationKey;
    }
  }

  /**
   * get translated string with formatting, using ES6 template
   * @param {Object|String} dict the input dictionary, which contains values with language codes
   * as keys, e.g. <code>{ en: 'hello', hk: 'world' }</code><p>
   * @param {Object} [args] key-values to apply. a dynamic dictionary object {@link #dict} will be inserted as
   * 'i18n' if the key 'i18n' is not given in args
   * @param {Object} [options] template compilation options
   */
  F(dict, args, options) {
    dict = this.S(dict);
    if (typeof dict === 'string') {
      let fn = es6compile(dict);
      args = args || {};
      if (args['i18n'] === undefined) {
        args['i18n'] = this.dict;
      }
      return this.nn.apply(this, es6resolve(fn, args, options));
    } else {
      return dict;
    }
  }

  /**
   * same as {@link #f}, but apply recursively if the translated string contains nested placeholders.
   * @param translationKey
   * @param [args]
   * @param {Object} [args] key-values to apply
   * @param {Object} [options] template compilation options
   * @param {Number} [maxDepth] default 2
   * 0 = no recursion (same as {@link #f}),
   * -1 = unlimited !! CAUTION: this can cause out of memory if an infinite loop is encountered!
   */
  fr(translationKey, args, options, maxDepth) {
    if (!Number.isFinite(maxDepth)) maxDepth = 2;
    if (!options) {
      options = {partial: true};
    }
    let result = this.f(translationKey, args, options);
    while (maxDepth-- != 0) {
      let newResult = this.f(result, args, options);
      if (newResult == result) {
        break; // cannot format anymore
      }
      result = newResult;
    }
    return result;
  }

  /**
   * same as {@link #f}, but apply recursively if the translated string contains nested placeholders.
   * @param {Object|String} dict the input dictionary, which contains values with language codes
   * as keys, e.g. <code>{ en: 'hello', hk: 'world' }</code><p>
   * @param {Object} [args] key-values to apply
   * @param {Object} [options] template compilation options
   * @param {Number} [maxDepth] default 2
   * 0 = no recursion (same as {@link #f}),
   * -1 = unlimited !! CAUTION: this can cause out of memory if an infinite loop is encountered!
   */
  FR(dict, args, options, maxDepth) {
    if (!Number.isFinite(maxDepth)) maxDepth = 2;
    if (!options) {
      options = {partial: true};
    }
    let result = this.F/* use custom dict first*/(dict, args, options);
    while (maxDepth-- != 0) {
      let newResult = this.f/* use internal dict for further translations*/(result, args, options);
      if (newResult == result) {
        break; // cannot format anymore
      }
      result = newResult;
    }
    return result;
  }

  /**
   * get a dynamic dictionary that can be used as a plain object but will not throw error if key not found
   */
  get dict() {
    return new Dynamic(this, []);
  }

  /**
   * return the available language codes
   * @return {String[]}
   */
  get langs() {
    return Object.keys(this.lang);
  }

  /**
   * Vue.use(i18n)
   * @param Vue
   */
  install(Vue) {
    Vue.mixin({
      computed: {
        i18n: () => this,
      },
    });
  }
}

/**
 * A helper proxy object to an I18n dictionary, for getting deep object paths without throwing error
 * @param i18n
 * @param path
 * @return {*}
 * @constructor
 */
function Dynamic(i18n, path) {
  this.path = path;
  this.i18n = i18n;
  return new Proxy(this, {
    set(self, prop, value, receiver) {
      console.warn('[i18n]', 'Attempted to modify dictionary: { key:', self.path.concat([prop]).join('.'), '}');
      return false;
    },
    has(self, prop) {
      return self.i18n.s(self.path.concat([prop]), null) !== null;
    },
    get(self, prop, receiver) {
      if (self[prop] === undefined) {
        return new Dynamic(self.i18n, self.path.concat([prop]));
      } else {
        return self[prop];
      }
    },
  });
}

Dynamic.prototype[Symbol.toPrimitive]
  = Dynamic.prototype.valueOf
  = Dynamic.prototype.toString
  = function () {
  return this.i18n.s(this.path, this.path.join('.'));
};

let _default = new i18n();
export default _default;
export {i18n};



// WEBPACK FOOTER //
// ./src/i18n/index.js